/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { ApolloError, useLazyQuery, useMutation } from '@apollo/client'
import {
  NEXT_CLASSIFICATION_CORRECTION,
  RELEASE_BOUNDING_BOX_LEASES,
  SUBMIT_CLASSIFICATION_CORRECTION,
  UltraNextClassificationCorrectionQuery,
  UltraNextClassificationCorrectionQueryVariables,
  UltraReleaseBoundingBoxLeasesMutation,
  UltraReleaseBoundingBoxLeasesMutationVariables,
  UltraSubmitClassificationCorrectionMutation,
  UltraSubmitClassificationCorrectionMutationVariables,
} from '@dai/graphql'
import React from 'react'
import { useSearchParams } from 'react-router-dom'

import type {
  ClassificationCorrection,
  CorrectionLeaseLockError,
  CorrectionNoItemsAvailableError,
  NextClassificationCorrectionResult,
} from './correctionTypes'

type CorrectionWorkflowOptions = {
  manageLeases?: boolean
  manageInitialCorrectionLease?: boolean
  onSubmitSuccess?: () => Promise<unknown> | unknown
}

const getOperationInfoMessage = (
  operationInfo: Extract<
    NonNullable<
      UltraSubmitClassificationCorrectionMutation['submitClassificationCorrection']
    >,
    { __typename: 'OperationInfo' }
  >,
) =>
  operationInfo.messages
    .map(({ message }) => message.trim())
    .filter(Boolean)
    .join('\n')

export const useCorrectionWorkflow = (
  initialCorrection: ClassificationCorrection | null = null,
  {
    manageLeases = true,
    manageInitialCorrectionLease = manageLeases,
    onSubmitSuccess,
  }: CorrectionWorkflowOptions = {},
) => {
  const [searchParams] = useSearchParams()
  const requestedUpc = searchParams.get('upc')?.trim() || null
  const [selectedPreviewBoxId, setSelectedPreviewBoxId] = React.useState<
    string | null
  >(null)
  const [result, setResult] =
    React.useState<NextClassificationCorrectionResult | null>(initialCorrection)
  const [loading, setLoading] = React.useState(false)
  const [submitTransitioning, setSubmitTransitioning] = React.useState(false)
  const [error, setError] = React.useState<ApolloError | null>(null)
  const currentBoxIdRef = React.useRef<string | null>(null)
  const initialCorrectionBoxIdRef = React.useRef(
    initialCorrection?.crop.id ?? null,
  )
  const latestRequestIdRef = React.useRef(0)

  const [loadCorrection] = useLazyQuery<
    UltraNextClassificationCorrectionQuery,
    UltraNextClassificationCorrectionQueryVariables
  >(NEXT_CLASSIFICATION_CORRECTION, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
  })

  const [releaseBoundingBoxLeases] = useMutation<
    UltraReleaseBoundingBoxLeasesMutation,
    UltraReleaseBoundingBoxLeasesMutationVariables
  >(RELEASE_BOUNDING_BOX_LEASES, {
    context: { endPoint: 'mlUltra' },
  })
  const [submitClassificationCorrection, { loading: submitting }] = useMutation<
    UltraSubmitClassificationCorrectionMutation,
    UltraSubmitClassificationCorrectionMutationVariables
  >(SUBMIT_CLASSIFICATION_CORRECTION, {
    context: { endPoint: 'mlUltra' },
  })

  const item =
    result?.__typename === 'ClassificationCorrection'
      ? (result as ClassificationCorrection)
      : null
  const leaseLock =
    result?.__typename === 'LeaseLockError'
      ? (result as CorrectionLeaseLockError)
      : null
  const noItems =
    result?.__typename === 'NoItemsAvailableError'
      ? (result as CorrectionNoItemsAvailableError)
      : null

  const releaseCorrectionLease = React.useCallback(
    async (boxId: string | null) => {
      if (!manageLeases) return
      if (
        !manageInitialCorrectionLease &&
        boxId === initialCorrectionBoxIdRef.current
      ) {
        return
      }
      if (!boxId) return
      try {
        await releaseBoundingBoxLeases({ variables: { boxIds: [boxId] } })
      } catch (releaseError) {
        console.error('Failed to release correction lease', releaseError)
      }
    },
    [manageInitialCorrectionLease, manageLeases, releaseBoundingBoxLeases],
  )

  const requestCorrection = React.useCallback(async () => {
    const requestId = latestRequestIdRef.current + 1
    latestRequestIdRef.current = requestId
    setLoading(true)
    setError(null)

    try {
      const response = await loadCorrection({
        variables: { upc: requestedUpc },
      })
      if (latestRequestIdRef.current !== requestId) {
        return null
      }

      const nextResult = response.data?.nextClassificationCorrection ?? null
      setResult(nextResult)
      setLoading(false)
      return nextResult
    } catch (loadError) {
      if (latestRequestIdRef.current !== requestId) {
        return null
      }

      setResult(null)
      setError(loadError as ApolloError)
      setLoading(false)
      throw loadError
    }
  }, [loadCorrection, requestedUpc])

  const handleCropFlagged = React.useCallback(
    async (_boxId: string) => {
      currentBoxIdRef.current = null
      setSelectedPreviewBoxId(null)
      await requestCorrection()
    },
    [requestCorrection],
  )

  const handleSubmitCorrection = React.useCallback(
    async (correctedUpc: string) => {
      const boxId = currentBoxIdRef.current
      if (!boxId) {
        throw new Error('No correction item is currently loaded.')
      }

      setSubmitTransitioning(true)
      try {
        const response = await submitClassificationCorrection({
          variables: {
            input: {
              boxId,
              correctedUpc,
            },
          },
        })

        const submitResult = response.data?.submitClassificationCorrection
        if (
          submitResult?.__typename !== 'SubmitClassificationCorrectionResult'
        ) {
          const operationInfoMessage =
            submitResult?.__typename === 'OperationInfo'
              ? getOperationInfoMessage(submitResult)
              : null
          throw new Error(
            operationInfoMessage ||
              'Submit classification correction did not succeed',
          )
        }

        const successfulSubmitResult = submitResult as Extract<
          NonNullable<
            UltraSubmitClassificationCorrectionMutation['submitClassificationCorrection']
          >,
          { __typename: 'SubmitClassificationCorrectionResult' }
        >

        if (!successfulSubmitResult.success) {
          throw new Error('Submit classification correction did not succeed')
        }

        currentBoxIdRef.current = null
        setSelectedPreviewBoxId(null)
        if (onSubmitSuccess) {
          await onSubmitSuccess()
          return
        }
        await requestCorrection()
      } finally {
        setSubmitTransitioning(false)
      }
    },
    [onSubmitSuccess, requestCorrection, submitClassificationCorrection],
  )

  React.useEffect(() => {
    initialCorrectionBoxIdRef.current = initialCorrection?.crop.id ?? null
    if (initialCorrection) {
      setResult(initialCorrection)
      return
    }
    void requestCorrection()
  }, [initialCorrection, requestCorrection])

  React.useEffect(() => {
    const nextBoxId = item?.crop.id ?? null
    const previousBoxId = currentBoxIdRef.current

    if (previousBoxId && previousBoxId !== nextBoxId) {
      void releaseCorrectionLease(previousBoxId)
    }

    currentBoxIdRef.current = nextBoxId
    setSelectedPreviewBoxId(current => (current === nextBoxId ? current : null))
  }, [item?.crop.id, releaseCorrectionLease])

  React.useEffect(
    () => () => {
      void releaseCorrectionLease(currentBoxIdRef.current)
    },
    [releaseCorrectionLease],
  )

  return {
    correction: item,
    crop: item?.crop ?? null,
    error,
    handleCropFlagged,
    handleRetry: requestCorrection,
    handleSubmitCorrection,
    leaseLock,
    loading,
    noItems,
    submitting: submitting || submitTransitioning,
    selectedPreviewBox:
      item?.crop && item.crop.id === selectedPreviewBoxId ? item.crop : null,
    selectedPreviewBoxId,
    setSelectedPreviewBoxId,
  }
}
